import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Sun, Moon, LogOut, Search, ShieldCheck, Command } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useTenant } from "@/contexts/TenantContext";
import { usePermissions } from "@/contexts/PermissionsContext";
import TenantSwitcher from "@/components/TenantSwitcher";
import TeamChatPopover from "@/components/TeamChat";
import { ADMIN_NAV, buildDomains } from "@/admin/admin-nav";
import { moduleForPath } from "@/admin/modules";
import { hexToHslTriplet } from "@/lib/app-utils";
import { useMissedCallbackAlerts } from "@/hooks/useMissedCallbackAlerts";

const C7_SMALL_LOGO = "/c7-mark.png";
const CONNECT7_WORDMARK = "/connect7-wordmark.png";
// Premium dark-blue default for Admin/login buttons (admin-overridable via Branding).
const DEFAULT_ADMIN_BUTTON_COLOR = "#1E40AF";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { tenant } = useTenant();
  const { levelFor, loading: permsLoading } = usePermissions();
  const brandLogo = tenant?.branding?.logo_wordmark_url || CONNECT7_WORDMARK;
  const nav = useNavigate();
  const location = useLocation();

  // Alert supervisors/admins in the console when a scheduled callback is
  // missed (no agent ready at the scheduled time).
  useMissedCallbackAlerts();

  // Admin + login use a premium dark-blue primary (buttons/accents) instead of
  // the red agent theme. Applied to :root so portals (dropdowns/dialogs/toasts)
  // inherit too; reverted on unmount so the agent workspace keeps its red theme.
  useEffect(() => {
    const hsl = hexToHslTriplet(tenant?.branding?.admin_button_color || DEFAULT_ADMIN_BUTTON_COLOR);
    const root = document.documentElement;
    if (hsl) { root.style.setProperty("--primary", hsl); root.style.setProperty("--ring", hsl); }
    return () => { root.style.removeProperty("--primary"); root.style.removeProperty("--ring"); };
  }, [tenant]);
  const searchRef = useRef(null);
  const [search, setSearch] = useState("");
  const [openDomains, setOpenDomains] = useState({ workspace: true });
  const searching = search.trim().length > 0;

  const isSuperSuper = user?.role === "super_super_admin";

  // Filter sections + items by the dynamic access-control matrix: hide any
  // module whose effective level is `hidden`. The Operator section stays
  // super_super_admin-only (not configurable). Search filters within that.
  const sections = useMemo(() => {
    const matchSearch = (it, s) => !search ||
      it.label.toLowerCase().includes(search.toLowerCase()) ||
      s.label.toLowerCase().includes(search.toLowerCase());
    return ADMIN_NAV
      .filter((s) => s.label !== "Operator" || isSuperSuper)
      .map((s) => {
        const items = (s.items || []).filter((it) => {
          const key = (it.to || "").split("?")[0];
          if (levelFor(key) === "hidden") return false;
          return matchSearch(it, s);
        });
        return { ...s, items };
      })
      .filter((s) => s.items.length > 0);
  }, [levelFor, isSuperSuper, search]);

  // Route-level guard: if the current page maps to a module that is hidden for
  // this user, bounce them back to the admin dashboard.
  useEffect(() => {
    if (permsLoading) return;
    const mod = moduleForPath(location.pathname);
    if (mod && levelFor(mod) === "hidden") {
      nav("/admin", { replace: true });
    }
  }, [location.pathname, permsLoading, levelFor, nav]);

  const domains = useMemo(() => buildDomains(sections), [sections]);

  // Auto-open the domain that owns the current route (plus Workspace).
  const activeDomainKey = useMemo(() => {
    for (const d of domains) {
      for (const s of d.sectionList) {
        if (s.items.some((it) => location.pathname === it.to || (it.to !== "/admin" && location.pathname.startsWith(it.to)))) {
          return d.key;
        }
      }
    }
    return "workspace";
  }, [domains, location.pathname]);

  useEffect(() => {
    setOpenDomains((o) => ({ ...o, workspace: true, [activeDomainKey]: true }));
  }, [activeDomainKey]);

  // Cmd/Ctrl + K focuses the admin search.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="min-h-screen flex bg-background text-foreground">
        {/* Sidebar */}
        <aside className="hidden lg:flex flex-col w-72 border-r border-border bg-card sticky top-0 h-screen">
          <div className="px-4 h-16 flex items-center gap-2.5 border-b border-border">
            <NavLink to="/admin" className="flex items-center" data-testid="admin-brand-link">
              <span className="inline-flex items-center dark:bg-white dark:rounded-md dark:px-2 dark:py-1">
                <img src={brandLogo} alt="Connect7 by Haloocom" className="h-9 w-auto max-w-[210px] object-contain" />
              </span>
            </NavLink>
          </div>

          <div className="p-3">
            <button
              type="button"
              data-testid="global-search-trigger"
              onClick={() => searchRef.current?.focus()}
              className="w-full flex items-center gap-2 h-9 px-2.5 rounded-lg border border-border bg-muted/40 text-muted-foreground hover:border-primary/40 hover:bg-muted/70 transition-colors"
            >
              <Search className="w-3.5 h-3.5 shrink-0" />
              <input
                ref={searchRef}
                data-testid="admin-search-input"
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 min-w-0 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
              />
              <kbd className="hidden xl:inline-flex items-center gap-0.5 text-[10px] font-mono font-medium text-muted-foreground border border-border rounded px-1 py-0.5 shrink-0">
                <Command className="w-2.5 h-2.5" />K
              </kbd>
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-2.5 pb-4 scroll-fade">
            {domains.map((dom) => {
              const DomIcon = dom.icon;
              const isOpen = searching || openDomains[dom.key];
              return (
                <div key={dom.key} className="mb-0.5">
                  <button
                    type="button"
                    data-testid={`sidebar-domain-${dom.key}`}
                    onClick={() => setOpenDomains((o) => ({ ...o, [dom.key]: !o[dom.key] }))}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                      isOpen ? "text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                  >
                    <DomIcon className={`w-[18px] h-[18px] shrink-0 ${isOpen ? "text-primary" : ""}`} strokeWidth={1.75} />
                    <span className="flex-1 text-left">{dom.label}</span>
                    <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${isOpen ? "" : "-rotate-90"}`} />
                  </button>

                  {isOpen && (
                    <div className="mt-0.5 mb-2 pl-3 space-y-3">
                      {dom.sectionList.map((sec) => (
                        <div key={sec.label}>
                          <div className="px-2.5 pb-1 text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground/70">
                            {sec.label}
                          </div>
                          <div className="space-y-0.5">
                            {sec.items.map((it) => {
                              const Icon = it.icon;
                              return (
                                <NavLink
                                  key={it.to}
                                  to={it.to}
                                  end={it.end}
                                  data-testid={`admin-nav-${it.to.replace(/^\/admin\/?/, "") || "overview"}`}
                                  className={({ isActive }) =>
                                    `group relative flex items-center gap-2.5 pl-4 pr-2.5 py-1.5 rounded-md text-sm transition-colors ${
                                      isActive
                                        ? "bg-primary/10 text-foreground font-medium before:absolute before:left-1 before:top-1/2 before:-translate-y-1/2 before:h-4 before:w-0.5 before:rounded-full before:bg-primary"
                                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                                    }`
                                  }
                                >
                                  <Icon className="w-4 h-4 shrink-0 transition-transform group-hover:translate-x-0.5" strokeWidth={1.75} />
                                  <span className="truncate">{it.label}</span>
                                </NavLink>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Main column */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Topbar */}
          <header className="sticky top-0 z-30 bg-card/80 glass border-b border-border">
            <div className="flex items-center gap-2.5 px-4 sm:px-6 h-16">
              {/* Mobile: brand */}
              <NavLink to="/admin" className="flex items-center lg:hidden">
                <span className="inline-flex items-center dark:bg-white dark:rounded-md dark:px-1.5 dark:py-1">
                  <img src={brandLogo} alt="Connect7 by Haloocom" className="h-8 w-auto max-w-[160px] object-contain" />
                </span>
              </NavLink>

              {/* Admin badge */}
              <div className="hidden md:flex h-9 items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 text-xs text-primary font-semibold" data-testid="admin-mode-badge">
                <ShieldCheck className="w-3.5 h-3.5" />
                Admin Console
              </div>

              {/* Tenant switcher (only super_super_admin) */}
              {isSuperSuper && <TenantSwitcher />}

              <div className="ml-auto flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button data-testid="admin-theme-toggle" onClick={toggle} variant="ghost" size="icon" className="rounded-full">
                      {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{theme === "dark" ? "Switch to light" : "Switch to dark"}</TooltipContent>
                </Tooltip>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="admin-user-menu" className="flex items-center gap-2 px-2 rounded-full hover:bg-muted h-10">
                      <Avatar className="h-7 w-7">
                        <AvatarFallback style={{ background: user?.avatar_color || "#0A195C", color: "white" }} className="text-[11px] font-bold">
                          {user?.initials}
                        </AvatarFallback>
                      </Avatar>
                      <div className="hidden sm:block text-left leading-tight">
                        <div className="text-overline text-muted-foreground">
                          {user?.role?.replace(/_/g, " ")}
                        </div>
                        <div className="text-sm font-medium">{user?.name}</div>
                      </div>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                    {isSuperSuper && tenant?.name && (
                      <div className="px-2 pb-1.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                        Tenant: <span className="font-bold text-foreground">{tenant.name}</span>
                      </div>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} data-testid="admin-logout-btn">
                      <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          <main className="flex-1 min-w-0">
            <Outlet />
          </main>
        </div>

        {/* Floating team chat — bottom-right of the admin console */}
        <div className="fixed bottom-6 right-6 z-50 hidden md:block" data-testid="admin-team-chat-dock">
          <TeamChatPopover />
        </div>
      </div>
    </TooltipProvider>
  );
}
